/**
 * Stylised inline-SVG glyphs: one per gear slot, gems, currencies. 24×24 viewBox, drawn with
 * `currentColor`, so a parent's `color` (usually `var(--rarity)`) tints them. No art assets.
 */
import type { GemColor, ItemSlot } from '@keystone/progression';
import { svg } from './dom';

const wrap = (body: string, cls = ''): string =>
  `<svg class="kui-glyph ${cls}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true">${body}</svg>`;

/** Shared fill: a translucent wash of the stroke colour. */
const F = 'fill="currentColor" fill-opacity="0.18"';

export const SLOT_GLYPH_BODY: Readonly<Record<ItemSlot, string>> = {
  weapon: `<path ${F} d="M20 3.5 19 9l-9 9-3-3 9-9z"/><path d="M5 13.5l5.5 5.5M7.8 16.2 4 20"/><path d="M16 6l2 2"/>`,
  helm: `<path ${F} d="M4 15a8 8 0 0 1 16 0v4h-5.2v-4.2H9.2V19H4z"/><path d="M12 7v7.8M8 11h8"/>`,
  chest: `<path ${F} d="M8 4l4 2 4-2 4 3-2.2 4V20H6.2v-9L4 7z"/><path d="M12 6v14M9 13h6"/>`,
  legs: `<path ${F} d="M7 4h10l1.2 16h-4.4L12 10.5 10.2 20H5.8z"/><path d="M7 7h10"/>`,
  gloves: `<path ${F} d="M8 20v-7.5L5.8 9.6a1.3 1.3 0 0 1 2-1.5L9 9.4V4.8a1.2 1.2 0 0 1 2.4 0V9V3.8a1.2 1.2 0 0 1 2.4 0V9V5a1.2 1.2 0 0 1 2.4 0v8.5L15.5 20z"/><path d="M8 17h7.6"/>`,
  boots: `<path ${F} d="M8 3h6v10.5l5.2 2.8V20H5.5v-3.5L8 13z"/><path d="M8 8h6M5.5 17.5h13.7"/>`,
  ring: `<circle ${F} cx="12" cy="14.5" r="5.5"/><circle cx="12" cy="14.5" r="3"/><path ${F} d="M9.2 6.2 12 3l2.8 3.2L12 9z"/>`,
  trinket: `<path d="M6.5 3 12 9.5 17.5 3"/><path ${F} d="M12 9.5l5 5.2L12 21l-5-6.3z"/><path d="M12 12.5v5.5"/>`,
};

export function slotGlyphSvg(slot: ItemSlot): SVGSVGElement {
  return svg(wrap(SLOT_GLYPH_BODY[slot] ?? SLOT_GLYPH_BODY.trinket, `slot-${slot}`));
}

export const GEM_COLOR_HEX: Readonly<Record<GemColor, string>> = { ruby: '#ff4d6d', sapphire: '#4d8dff', emerald: '#2fd38a' };
export const GEM_LABEL: Readonly<Record<GemColor, string>> = { ruby: 'Ruby', sapphire: 'Sapphire', emerald: 'Emerald' };

/** Faceted gem; colour from the gem, `rank` adds facet lines (1..5). */
export function gemGlyphSvg(color: GemColor, rank = 1): SVGSVGElement {
  const facets = rank >= 3 ? '<path d="M9 9l3 11 3-11"/>' : '<path d="M12 9v11"/>';
  const s = svg(wrap(`<path fill="currentColor" fill-opacity="0.45" d="M5.5 9 9 4.5h6L18.5 9 12 20.5z"/><path d="M5.5 9h13"/>${facets}`, `gem gem-${color}`));
  s.style.color = GEM_COLOR_HEX[color];
  return s;
}

export type CurrencyKind = 'gold' | 'shards' | 'xp' | 'rating' | 'gem';

const CURRENCY_BODY: Readonly<Record<CurrencyKind, string>> = {
  gold: `<circle fill="currentColor" fill-opacity="0.35" cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="5"/><path d="M12 9.5v5"/>`,
  shards: `<path fill="currentColor" fill-opacity="0.35" d="M12 3l5 7-5 11-5-11z"/><path d="M7 10h10M12 3v18"/>`,
  xp: `<path fill="currentColor" fill-opacity="0.3" d="M12 3l2.6 5.6 6 .7-4.5 4.1 1.2 6L12 16.4 6.7 19.4l1.2-6L3.4 9.3l6-.7z"/>`,
  rating: `<path fill="currentColor" fill-opacity="0.3" d="M12 3l7 3v5.5c0 4.5-3 7.8-7 9.5-4-1.7-7-5-7-9.5V6z"/><path d="M9 12l2 2 4-4"/>`,
  gem: `<path fill="currentColor" fill-opacity="0.35" d="M5.5 9 9 4.5h6L18.5 9 12 20.5z"/><path d="M5.5 9h13"/>`,
};

export const CURRENCY_COLOR: Readonly<Record<CurrencyKind, string>> = {
  gold: '#f5c542',
  shards: '#7fe3ff',
  xp: '#c59bff',
  rating: '#ffb547',
  gem: '#ff6b8a',
};

export function currencyGlyphSvg(kind: CurrencyKind): SVGSVGElement {
  const s = svg(wrap(CURRENCY_BODY[kind], `cur-${kind}`));
  s.style.color = CURRENCY_COLOR[kind];
  return s;
}

/** Small UI icons (nav buttons, back arrow). */
export type UiIcon = 'back' | 'character' | 'talents' | 'inventory' | 'gems' | 'vault' | 'rank' | 'play' | 'export' | 'import' | 'reset' | 'close' | 'lock' | 'check';
const ICON_BODY: Readonly<Record<UiIcon, string>> = {
  back: '<path d="M15 5l-7 7 7 7"/>',
  character: '<circle cx="12" cy="8" r="4"/><path d="M4.5 20.5c1-4.2 4-6.5 7.5-6.5s6.5 2.3 7.5 6.5"/>',
  talents: '<circle cx="6" cy="6" r="2.2"/><circle cx="18" cy="6" r="2.2"/><circle cx="12" cy="18" r="2.2"/><path d="M7.5 7.8 10.6 16M16.5 7.8 13.4 16M8.2 6h7.6"/>',
  inventory: '<path d="M5 8h14l-1.2 12H6.2z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/>',
  gems: '<path d="M5.5 9 9 4.5h6L18.5 9 12 20.5z"/><path d="M5.5 9h13M12 9v11.5"/>',
  vault: '<rect x="3.5" y="5" width="17" height="14" rx="2"/><circle cx="12" cy="12" r="3.2"/><path d="M12 8.8V7M12 17v-1.8M15.2 12H17M7 12h1.8"/>',
  rank: '<path d="M12 3l7 3v5.5c0 4.5-3 7.8-7 9.5-4-1.7-7-5-7-9.5V6z"/><path d="M8.5 11.5 12 9l3.5 2.5M8.5 15 12 12.5l3.5 2.5"/>',
  play: '<path fill="currentColor" d="M8 5.5v13l10.5-6.5z"/>',
  export: '<path d="M12 15V4M7.5 8.5 12 4l4.5 4.5"/><path d="M5 14v5h14v-5"/>',
  import: '<path d="M12 4v11M7.5 10.5 12 15l4.5-4.5"/><path d="M5 14v5h14v-5"/>',
  reset: '<path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3"/><path d="M4.5 4.5v4h4"/>',
  close: '<path d="M6 6l12 12M18 6 6 18"/>',
  lock: '<rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>',
  check: '<path d="M5 12.5l4.5 4.5L19 7.5"/>',
};

export function iconSvg(name: UiIcon): SVGSVGElement {
  return svg(wrap(ICON_BODY[name], `icon-${name}`));
}
